import { Component, OnInit } from '@angular/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { CalendarOptions, DatesSetArg, EventClickArg, EventSourceInput } from '@fullcalendar/core';
import { PickupService } from '../../../model/shared-entities/pickups/pickup.service';
import { IPickupForSearch } from '../../../model/interfaces/custom/pickup-for-search';
import { PickupStatus } from '../../order-basic-info/pickup-status-enum';
import { IModalWrapperApi } from '@mt-ng2/modal-module';
import { CarrierService } from '../../../carriers/services/carrier.service';
import { WarehouseService } from '../../../warehouses/services/warehouse.service';
import { CustomerService } from '../../../customers/customer.service';
import { MtSearchFilterItem } from '@mt-ng2/search-filter-select-control';
import { forkJoin } from 'rxjs';
import { CommonFunctions } from '../../../common/services/common-functions.service';
import { IWarehouse } from '../../../model/interfaces/warehouse';

@Component({
    selector: 'app-pickup-calendar',
    templateUrl: './pickup-calendar.component.html',
    styleUrls: ['./pickup-calendar.component.css'],
    standalone: false
})
export class PickupCalendarComponent implements OnInit {
    calendarOptions: CalendarOptions = {
        initialView: 'timeGridWeek',
        plugins: [dayGridPlugin, timeGridPlugin],
        events: [],
        eventClick: this.handleEventClick.bind(this),
        datesSet: this.handleDatesSet.bind(this),
        height: 'auto',
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridDay,timeGridWeek,dayGridMonth',
        },
    };

    modalOptions = {
        text: '',
        title: '',
        width: '70%',
        showCancelButton: false,
        showConfirmButton: false,
    };

    statusColors = {
        [PickupStatus.Requested]: '#f90',
        [PickupStatus.Confirmed]: '#528be9',
        [PickupStatus.CheckedIn]: '#6aa84f',
    };

    start: string;
    end: string;

    pickupsByEventId: { [key: string]: IPickupForSearch[] } = {};
    selectedPickups: IPickupForSearch[] = [];
    pickupCalendarTableDialogApi: IModalWrapperApi;

    pickupStatusMetaItems: MtSearchFilterItem[] = [];
    customerMetaItems: MtSearchFilterItem[] = [];
    carrierMetaItems: MtSearchFilterItem[] = [];
    warehouseMetaItems: MtSearchFilterItem[] = [];
    warehouses: IWarehouse[] = [];

    constructor(
        private pickupService: PickupService,
        private customerService: CustomerService,
        private carrierService: CarrierService,
        private warehouseService: WarehouseService,
    ) {}

    ngOnInit(): void {
        forkJoin([
            this.customerService.getActiveCustomers(),
            this.carrierService.getSimpleCarriers(),
            this.warehouseService.getUserWarehouses(),
            this.pickupService.getPickupStatuses(),
        ]).subscribe(([customers, carriers, warehouses, statuses]) => {
            this.customerMetaItems = CommonFunctions.mapMtSearchFilterItems(customers);

            this.carrierMetaItems = CommonFunctions.mapMtSearchFilterItems(carriers);

            this.warehouses = warehouses;

            this.warehouseMetaItems = warehouses.map((w) => new MtSearchFilterItem({ Id: w.Id, Name: w.WarehouseId }, false));

            this.pickupStatusMetaItems = CommonFunctions.mapMtSearchFilterItems(statuses);
        });
    }

    handleDatesSet(arg: DatesSetArg): void {
        this.start = arg.start.toDateString();
        this.end = arg.end.toDateString();
        this.fetchEvents();
    }

    fetchEvents(): void {
        const selectedCustomerIds = this.getSelectedFilters(this.customerMetaItems);

        const selectedCarrierIds = this.getSelectedFilters(this.carrierMetaItems);

        const selectedStatusIds = this.getSelectedFilters(this.pickupStatusMetaItems);

        const selectedWarehouseIds = this.warehouseMetaItems.filter((item) => item.Selected).map((item) => item.Item.Id);

        const dto = {
            start: this.start,
            end: this.end,
            customerIds: selectedCustomerIds,
            carrierIds: selectedCarrierIds,
            warehouseIds: selectedWarehouseIds,
            statusIds: selectedStatusIds,
        };

        this.pickupService.getPickupsForCalendar(dto).subscribe((pickups) => {
            const events = this.groupPickupsByDateAndStatus(pickups);
            this.calendarOptions.events = events;
        });
    }

    groupPickupsByDateAndStatus(pickups: IPickupForSearch[]): EventSourceInput {
        const grouped = {};
        let eventId = 1;
        const events: EventSourceInput = [];

        pickups.forEach((pickup) => {
            const date = pickup.StatusId === PickupStatus.Requested ? pickup.DateRequested?.toString() : pickup.DateConfirmed?.toString();

            if (!date) return;

            if (!grouped[date]) {
                grouped[date] = {};
            }

            const status = pickup.StatusId;

            if (!grouped[date][status]) {
                grouped[date][status] = [];
            }

            grouped[date][status].push(pickup);
        });

        for (const date in grouped) {
            for (const status in grouped[date]) {
                eventId++;

                const pickupsInGroup = grouped[date][status];
                const isAllDay = Number(status) === PickupStatus.Requested;

                const title = pickupsInGroup.length === 1 ? `1 Pickup` : `${pickupsInGroup.length} Pickups`;

                events.push({
                    id: eventId.toString(),
                    title: title,
                    start: date,
                    end: date,
                    color: this.statusColors[status],
                    allDay: isAllDay,
                });

                this.pickupsByEventId[eventId.toString()] = pickupsInGroup;
            }
        }

        return events;
    }

    handleEventClick(arg: EventClickArg): void {
        const eventId = arg.event.id;
        this.selectedPickups = this.pickupsByEventId[eventId];
        this.modalOptions.title = arg.event.title;
        this.pickupCalendarTableDialogApi.show();
    }

    closePickupCalendarDialog(): void {
        this.pickupCalendarTableDialogApi.close();
        this.selectedPickups = [];
    }

    private getSelectedFilters(filters: MtSearchFilterItem[]): number[] {
        return filters.filter((item) => item.Selected).map((item) => item.Item.Id);
    }
}
